import React, { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import styles from './AppleGooglePayButtons.module.css';
import PropTypes from 'prop-types';
import { inject, observer } from 'mobx-react';
import { useHistory } from 'react-router-dom';
import { api } from '../../../../services';
import { message, Spin } from 'antd';

// --- Zuora SDK Integration for Apple Pay and Google Pay ---
export const AppleGooglePayButtons = inject(
  'translationsStore',
  'mypageStore',
  'productsStore'
)(
  observer(
    ({ translationsStore, mypageStore, productsStore, disabled, termsRef }) => {
      const [paymentProcessing, setPaymentProcessing] = useState(false);
      const totalAmount = productsStore
        .calculateTotalAmountForProductsToSubscribe()
        .toString();

      const isProduction =
        process.env.REACT_APP_API_BASE_URL ===
        'https://air-master-api-production.myair.io';

      const [isApplePayAvailable, setIsApplePayAvailable] = useState(false);
      const [isGooglePayAvailable, setIsGooglePayAvailable] = useState(false);

      // DEBUG: mostra fallback (query param ?applePayDebug=1)
      const applePayDebugEnabled = useMemo(() => {
        return true;
      }, []);

      const [showApplePayFallback, setShowApplePayFallback] = useState(false);

      const applePayContainerRef = useRef(null);
      const googlePayContainerRef = useRef(null);
      const applePayButtonRef = useRef(null);
      const googlePayButtonRef = useRef(null);
      const isMountedRef = useRef(true);

      // Evita retry infiniti
      const applePayRetryRef = useRef(0);

      const history = useHistory();

      // Responsive width/height for iframe button
      const isMobile = window.matchMedia('(max-width: 1200px)').matches;

      const buttonWidth = '300px'; //isMobile ? '300px' : '180px';
      const buttonHeight = isMobile ? '56px' : '48px';
      const buttonBorderRadius = isMobile ? '8px' : '15px';

      // Get Zuora publishable key
      const getZuoraPublishableKey = useCallback(async () => {
        try {
          // First try to get from environment variable
          if (process.env.REACT_APP_ZUORA_PUBLISHABLE_KEY) {
            return process.env.REACT_APP_ZUORA_PUBLISHABLE_KEY;
          }

          console.error('No Zuora configuration available');
          return null;
        } catch (error) {
          console.error('Failed to get Zuora publishable key:', error);
          return null;
        }
      }, []);

      // Create payment session for Zuora
      const createPaymentSession = useCallback(() => {
        return new Promise((resolve, reject) => {
          // Validate required data before making the API call
          const zuoraAccountId = mypageStore?.remoteUserData?.zuora_account_id;

          if (!zuoraAccountId) {
            const error = new Error(
              'No Zuora account ID available for payment session'
            );
            console.error('Payment session error:', error.message);
            return reject(error);
          }

          if (!totalAmount || parseFloat(totalAmount) <= 0) {
            const error = new Error('Invalid amount for payment session');
            console.error(
              'Payment session error:',
              error.message,
              'Amount:',
              totalAmount
            );
            return reject(error);
          }

          // Call the new payment session API endpoint
          const payload = {
            zuora_account_id: zuoraAccountId,
            amount: parseFloat(totalAmount),
          };
          console.log('Creating payment session with payload:', payload);

          api('createZuoraPaymentSession', payload)
            .then((response) => {
              // Validate response structure
              if (!response || !response.token) {
                const error = new Error(
                  'Invalid payment session response: missing token'
                );
                console.error(
                  'Payment session error:',
                  error.message,
                  'Response:',
                  response
                );
                return reject(error);
              }

              console.log('==========');
              console.log('Got Payment Session Token');
              console.log('==========');
              console.log(response.token);

              // Zuora expects only the token to be returned
              return resolve(response.token);
            })
            .catch((error) => {
              console.error('Failed to create payment session:', error);

              // Handle specific error types
              let errorMessage = 'Failed to create payment session';
              if (error.response) {
                // Server responded with error status
                const status = error.response.status;
                const data = error.response.data;

                if (status === 401) {
                  errorMessage = 'Authentication failed for payment session';
                } else if (status === 400) {
                  errorMessage = `Invalid payment request: ${data?.message ||
                    'Bad request'}`;
                } else if (status === 403) {
                  errorMessage = 'Payment session access denied';
                } else if (status === 500) {
                  errorMessage = 'Payment service temporarily unavailable';
                } else {
                  errorMessage = `Payment session failed (${status}): ${data?.message ||
                    'Unknown error'}`;
                }
              } else if (error.request) {
                // Network error
                errorMessage =
                  'Network error: Unable to connect to payment service';
              } else if (error.message) {
                // Other errors
                errorMessage = `Payment session error: ${error.message}`;
              }

              const enhancedError = new Error(errorMessage);
              enhancedError.originalError = error;
              return reject(enhancedError);
            });
        });
      }, [mypageStore, totalAmount]);

      // Handle Google Pay completion
      const handlePayComplete = useCallback(
        async (result, method) => {
          if (result.success) {
            try {
              // Validate that we have the necessary payment information
              const paymentReference = result.paymentMethodId || result.refId;
              if (!paymentReference) {
                throw new Error(
                  'No payment method ID received from Google Pay'
                );
              }

              // Use the same subscription flow as credit card
              let route = `/thankyoupage`;

              // Check for different product types (same logic as credit card)
              if (
                productsStore.selectedProduct?.features?.some(
                  (feature) => feature.node.name === 'movein'
                ) &&
                productsStore.selectedProduct?.category === 'renew'
              ) {
                route = `${route}?movein=true&renewal=true`;
              } else if (
                productsStore.selectedProduct?.features?.some(
                  (feature) => feature.node.name === 'movein'
                )
              ) {
                route = `${route}?movein=true`;
              }
              setPaymentProcessing(true);
              await productsStore.subscribeToProduct(method, paymentReference);

              // Navigate to success page
              history.push(route);
              setPaymentProcessing(false);
            } catch (error) {
              console.error(method + ' transaction failed:', error);

              // Handle different types of subscription errors
              let errorMessage = translationsStore.translate(
                'common.genericError'
              );

              if (error.response) {
                const status = error.response.status;
                const data = error.response.data;

                if (status === 402) {
                  errorMessage = translationsStore.translate(
                    'payment.cardDeclined'
                  );
                } else if (status === 400) {
                  errorMessage = translationsStore.translate(
                    'payment.invalidRequest'
                  );
                } else if (status === 409) {
                  errorMessage = translationsStore.translate(
                    'payment.alreadySubscribed'
                  );
                } else if (status === 500) {
                  errorMessage = translationsStore.translate(
                    'payment.serverError'
                  );
                } else if (data?.message) {
                  errorMessage = data.message;
                }
              } else if (
                error.message &&
                error.message.includes('payment method')
              ) {
                errorMessage = translationsStore
                  .translate('payment.invalidPaymentMethod')
                  .replace('__METHOD__', method);
              } else if (error.message && error.message.includes('network')) {
                errorMessage = translationsStore.translate(
                  'payment.networkError'
                );
              }

              message.error({
                content: errorMessage,
                duration: 8,
                style: { marginTop: '100px' },
              });
            }
          } else {
            console.error(method + ' failed:', result);

            // Handle Apple Pay / Google Pay specific errors
            const error = result.error;
            let errorMessage = translationsStore
              .translate('payment.paymentFailedGeneric')
              .replace('__METHOD__', method);

            if (error) {
              // Check for specific Zuora error types
              if (
                (error.message && error.message.includes('401')) ||
                (error.message && error.message.includes('Unauthorized')) ||
                (error.message && error.message.includes('unauthorized_access'))
              ) {
                console.warn(
                  method + ' failed due to Zuora sandbox configuration issue'
                );

                if (!isProduction) {
                  message.error({
                    content: (
                      <div>
                        <strong>
                          {translationsStore
                            .translate('payment.sandboxNotConfiguredTitle')
                            .replace('__METHOD__', method)}
                        </strong>
                        <br />
                        <small>
                          {translationsStore
                            .translate('payment.sandboxNotConfiguredDesc')
                            .replace('__METHOD__', method)}
                        </small>
                      </div>
                    ),
                    duration: 8,
                    style: { marginTop: '100px' },
                  });
                  return;
                } else {
                  errorMessage = translationsStore
                    .translate('payment.temporarilyUnavailable')
                    .replace('__METHOD__', method);
                }
              } else if (error.message && error.message.includes('CANCELED')) {
                // User cancelled the payment
                console.log(method + ' cancelled by user');
                return; // Don't show error for user cancellation
              } else if (
                error.message &&
                error.message.includes('DEVELOPER_ERROR')
              ) {
                errorMessage = translationsStore.translate(
                  'payment.configurationError'
                );
              } else if (
                error.message &&
                error.message.includes('NETWORK_ERROR')
              ) {
                errorMessage = translationsStore.translate(
                  'payment.networkError'
                );
              } else if (error.message) {
                errorMessage = `${translationsStore
                  .translate('payment.methodErrorPrefix')
                  .replace('__METHOD__', method)}: ${error.message}`;
              }
            }

            message.error({
              content: errorMessage,
              duration: 8,
              style: { marginTop: '100px' },
            });
          }
        },
        [productsStore, history, translationsStore]
      );
      const handleApplePayComplete = useCallback(
        (r) => handlePayComplete(r, 'Apple Pay'),
        [handlePayComplete]
      );
      const handleGooglePayComplete = useCallback(
        (r) => handlePayComplete(r, 'Google Pay'),
        [handlePayComplete]
      );

      const inspectApplePayDom = useCallback(
        (label) => {
          const host = applePayContainerRef.current?.querySelector(
            'apple-pay-button'
          );
          const hasShadow = !!host?.shadowRoot;

          if (applePayDebugEnabled) {
            console.log(`[ApplePay][${label}] host=`, host);
            console.log(`[ApplePay][${label}] hasShadow=`, hasShadow);
            if (host) {
              const cs = window.getComputedStyle(host);
              console.log(`[ApplePay][${label}] computed`, {
                display: cs.display,
                visibility: cs.visibility,
                opacity: cs.opacity,
                width: cs.width,
                height: cs.height,
              });
            }
          }

          return { host, hasShadow };
        },
        [applePayDebugEnabled]
      );

      // Direct implementation based on Zuora docs
      const renderApplePayButton = useCallback(async () => {
        try {
          // Prevent double initialization
          if (applePayButtonRef.current) {
            console.log('Apple Pay button already initialized');
            return;
          }

          if (!window.Zuora) {
            console.error('Zuora SDK not loaded');
            setIsApplePayAvailable(false);
            return;
          }

          // Log di supporto: in prod questo ti dice subito se il browser “abilita” Apple Pay
          if (applePayDebugEnabled) {
            console.log('[ApplePay] ApplePaySession=', window.ApplePaySession);
            console.log(
              '[ApplePay] canMakePayments=',
              window.ApplePaySession?.canMakePayments?.()
            );
          }

          if (!window.ApplePaySession?.canMakePayments?.()) {
            console.log('Apple Pay is not available on this device/browser');
            setIsApplePayAvailable(false);
            return;
          }

          const publishableKey = await getZuoraPublishableKey();
          if (!publishableKey) {
            console.error('No publishable key available');
            setIsApplePayAvailable(false);
            return;
          }

          const zuora = window.Zuora(publishableKey);

          zuora
            .createApplePayButton({
              currency: 'EUR',
              amount: totalAmount,
              countryCode: 'IT',

              // Nota: prova anche 'it-IT' (BCP 47) se sospetti che it_IT dia edge case in prod
              locale: 'it-IT',

              supportedNetworks: ['visa', 'masterCard', 'amex', 'discover'],
              merchantInfo: { merchantName: 'Air srl' },
              buttonType: 'pay',
              buttonStyle: 'white-outline',
              buttonRadius: buttonBorderRadius,
              buttonHeight: buttonHeight,
              createPaymentSession,
              onComplete: handleApplePayComplete,
            })
            .then(async (applePayButton) => {
              applePayButtonRef.current = applePayButton;

              // Pulisci sempre (evita residui DOM / doppio mount)
              if (applePayContainerRef.current) {
                applePayContainerRef.current.innerHTML = '';
              }

              applePayButton.mount('#apple-pay-container');
              setIsApplePayAvailable(true);

              // Aspetta 2 frame e poi controlla se lo shadow DOM compare
              await new Promise((r) =>
                requestAnimationFrame(() => requestAnimationFrame(r))
              );
              const { hasShadow, host } = inspectApplePayDom('after-mount');

              // Se non c’è shadowRoot, tenta 1 retry e poi mostra fallback cliccabile
              if (!hasShadow && applePayRetryRef.current < 1) {
                applePayRetryRef.current += 1;
                applePayButtonRef.current = null;
                setTimeout(() => {
                  if (isMountedRef.current) renderApplePayButton();
                }, 150);
              } else if (!hasShadow && host) {
                setShowApplePayFallback(true);
              }
            })
            .catch((error) => {
              console.error('Apple Pay button creation failed:', error);
              setIsApplePayAvailable(false);
            });
        } catch (error) {
          console.error('Apple Pay setup error:', error);
          setIsApplePayAvailable(false);
        }
      }, [totalAmount, buttonBorderRadius]);

      // Setup Google Pay
      const setupGooglePay = useCallback(async () => {
        try {
          const publishableKey = await getZuoraPublishableKey();
          if (!publishableKey) {
            console.error('No publishable key available');
            return;
          }

          if (!window.Zuora) {
            console.error('setupGooglePay - Zuora SDK not loaded');
            throw new Error('Zuora SDK not available');
          }

          if (!publishableKey) {
            throw new Error('No publishable key provided for Google Pay setup');
          }

          const zuora = window.Zuora(publishableKey);

          const googlePayButton = await zuora.createGooglePayButton({
            currency: 'EUR',
            amount: totalAmount,
            countryCode: 'IT',
            locale: 'it_IT',
            allowedCardNetworks: ['AMEX', 'DISCOVER', 'MASTERCARD', 'VISA'],
            merchantInfo: {
              merchantName: 'Air srl',
            },
            buttonHeight,
            buttonWidth,
            buttonRadius: buttonBorderRadius,
            buttonColor: 'black',
            buttonType: 'plain',
            createPaymentSession: createPaymentSession,
            onComplete: handleGooglePayComplete,
          });

          if (!googlePayButton) {
            throw new Error('Failed to create Google Pay button');
          }

          if (!googlePayContainerRef.current) {
            throw new Error('Google Pay container not available');
          }

          googlePayButtonRef.current = googlePayButton;
          try {
            googlePayButton.mount(googlePayContainerRef.current);

            console.log('Google Pay button mounted successfully');
            setIsGooglePayAvailable(true);
          } catch (mountError) {
            console.error('Error mounting Google Pay button:', mountError);
            setIsGooglePayAvailable(false);
            throw new Error(
              `Failed to mount Google Pay button: ${mountError.message}`
            );
          }
        } catch (error) {
          setIsGooglePayAvailable(false);

          // Handle Google Pay not available gracefully - this is normal
          if (
            error.message &&
            (error.message.includes('not available on this device') ||
              error.message.includes('Google Pay is not available') ||
              error.message.includes('not supported'))
          ) {
            console.log(
              'Google Pay is not supported on this device - this is expected behavior'
            );
            return; // Don't show error message, this is normal
          }

          console.log('Google Pay setup failed:', error.message);

          // Handle other specific error types
          let errorMessage = translationsStore.translate(
            'payment.googlePaySetupFailed'
          );
          let shouldShowError = false;

          if (error.message.includes('SDK not available')) {
            errorMessage = translationsStore.translate('payment.sdkNotLoaded');
            shouldShowError = true;
          } else if (error.message.includes('publishable key')) {
            errorMessage = translationsStore.translate(
              'payment.paymentConfigError'
            );
            shouldShowError = true;
          } else if (error.message.includes('mount')) {
            errorMessage = translationsStore.translate(
              'payment.googlePayMountFailed'
            );
            shouldShowError = true;
          } else if (error.message.includes('network')) {
            errorMessage = translationsStore.translate(
              'payment.googlePayNetworkError'
            );
            shouldShowError = true;
          }

          // Only show error messages in development and for non-availability issues
          if (!isProduction && shouldShowError) {
            message.error({
              content: `${translationsStore.translate(
                'payment.googlePaySetupFailedPrefix'
              )}: ${errorMessage}`,
              duration: 8,
              style: { marginTop: '100px' },
            });
          }
        }
      }, [
        totalAmount,
        createPaymentSession,
        handleGooglePayComplete,
        buttonHeight,
        buttonWidth,
        buttonBorderRadius,
        isProduction,
        getZuoraPublishableKey,
        handleApplePayComplete,
        inspectApplePayDom,
        applePayDebugEnabled,
      ]);

      // Initialize Zuora with publishable key - simplified approach
      const initializeZuora = useCallback(async () => {
        if (!totalAmount) {
          console.error(
            'No amount specified for apple and google pay payment buttons'
          );
          return;
        }

        // Prevent re-initialization if buttons are already mounted
        if (applePayButtonRef.current || googlePayButtonRef.current) {
          console.log('Payment buttons already initialized, skipping...');
          return;
        }

        console.log(
          'Setting up APPLE AND GOOGLE payment buttons for amount:',
          totalAmount
        );
        renderApplePayButton();
        setupGooglePay();
      }, [renderApplePayButton, totalAmount, setupGooglePay]);

      useEffect(() => {
        const loadZuoraSDK = () => {
          if (window.Zuora) {
            initializeZuora();
            return;
          }
          const script = document.createElement('script');
          const environment = isProduction ? '' : 'sandbox/';
          script.src = `https://js.zuora.com/payment/${environment}1.4.0/zuora.js`;
          script.onload = initializeZuora;
          script.onerror = () => console.error('Failed to load Zuora SDK');
          document.head.appendChild(script);
        };

        loadZuoraSDK();

        return () => {
          try {
            if (applePayContainerRef.current)
              applePayContainerRef.current.innerHTML = '';
            if (googlePayContainerRef.current)
              googlePayContainerRef.current.innerHTML = '';
          } catch {}
          isMountedRef.current = false;
          applePayButtonRef.current = null;
          googlePayButtonRef.current = null;
          applePayRetryRef.current = 0;
          setShowApplePayFallback(false);
          setIsApplePayAvailable(false);
          setIsGooglePayAvailable(false);
        };
      }, [initializeZuora, isProduction]);

      // Calculate button availability for styling
      const totalAvailableButtons =
        (isApplePayAvailable ? 1 : 0) + (isGooglePayAvailable ? 1 : 0);
      const isSingleButton = true; //totalAvailableButtons === 1;

      return (
        <div className={styles.container}>
          {paymentProcessing && (
            <div className={styles.processingOverlay}>
              <Spin size='large' />
              {translationsStore.translate('paymentView.processingPayment')}
            </div>
          )}

          <div className={styles.buttonsContainer}>
            {/* APPLE PAY */}
           <div className={`${styles.buttonWrapper} ${disabled ? styles.disabledButton : ''}`}
            style={{ display: isApplePayAvailable ? 'block' : 'none' }}
           >
            <div
              ref={applePayContainerRef}
              id='apple-pay-container'
              style={{
                height: buttonHeight,
                width: '100%',
                position: 'relative',
                border: 'none',
                backgroundColor: 'transparent',
                overflow: 'visible',
              }}
            />

            {disabled && <div className={styles.disabledOverlay} />}

            {/* DEBUG fallback*/}
            {applePayDebugEnabled && showApplePayFallback && (
              <button
                type='button'
                onClick={() => {
                  const host = applePayContainerRef.current?.querySelector(
                    'apple-pay-button'
                  );
                  if (host) {
                    const event = new MouseEvent('click', {
                      view: window,
                      bubbles: true,
                      cancelable: true,
                    });
                    host.dispatchEvent(event);
                  }
                }}
                style={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  height: buttonHeight,
                  width: '100%',
                  borderRadius: buttonBorderRadius,
                  border: 0,
                  cursor: 'pointer',
                  WebkitAppearance: '-apple-pay-button',
                  WebkitApplePayButtonType: 'pay',
                  WebkitApplePayButtonStyle: 'white-outline',
                }}
              />
            )}
          </div>
            {/* GOOGLE PAY */}
            <div
              className={`${styles.buttonWrapper} ${disabled ? styles.disabledButton : ''}`}
            >
              <div
                ref={googlePayContainerRef}
                aria-label='Pay with Google Pay'
                id='google-pay-container'
                style={{ position: 'relative', width: '100%', height: buttonHeight }}
              >
                {disabled && <div className={styles.disabledOverlay} />}
              </div>
            </div>
          </div>

          {disabled && (
            <div
              className={styles.disabledMessage}
              onClick={() => {
                if (termsRef && termsRef.current) {
                  termsRef.current.scrollIntoView({
                    behavior: 'smooth',
                    block: 'start',
                  });
                }
              }}
            >
              <div className={styles.disabledMessageText}>
                {translationsStore.translate(
                  'paymentManagement.paymentDisabledMessage'
                )}
              </div>
            </div>
          )}

          {(isApplePayAvailable || isGooglePayAvailable) && (
            <div className={styles.dividerContainer}>
              <div className={styles.dividerLine} />
              <span className={styles.dividerText}>
                {translationsStore.translate('common.orPayAnotherWay')}
              </span>
              <div className={styles.dividerLine} />
            </div>
          )}
        </div>
      );
    }
  )
);

AppleGooglePayButtons.propTypes = {
  translationsStore: PropTypes.object,
  mypageStore: PropTypes.object,
  productsStore: PropTypes.object,
  history: PropTypes.object,
};
